import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { constant } from "common/constant";
import { globalStyles } from "common/theme/styles";
import { Field, Form, Formik } from "formik";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import CompanyPolicyEditor from "common/components/companyPolicyEditor";
import { recruitConstant } from "services/Recruit/constant";
import * as Yup from "yup";

// Helper component to update Formik values when subPolicyData loads
const FormikDataUpdater = ({ subPolicyData, isEditMode, loadingSubPolicy, setFieldValue }) => {
  useEffect(() => {
    if (isEditMode && subPolicyData && !loadingSubPolicy) {
      if (subPolicyData.title) {
        setFieldValue('title', subPolicyData.title);
      }
      if (subPolicyData.description) {
        setFieldValue('description', subPolicyData.description);
      }
      if (subPolicyData.content) {
        setFieldValue('content', subPolicyData.content);
      }
    }
  }, [subPolicyData, isEditMode, loadingSubPolicy, setFieldValue]);

  return null;
};

const CompanySubPolicyModel = ({ isOpen, onClose, info, handleFormSubmit }) => {
  const { addLoading, updateLoading, data } = useSelector((state) => state.companyPolicy);
  const [subPolicyData, setSubPolicyData] = useState(null);
  const [loadingSubPolicy, setLoadingSubPolicy] = useState(false);

  const colors = globalStyles.colors;
  const isEditMode = info.type === 'edit';
  const subPolicyId = info?.info?.subPolicyInfo?.id;
  const policyId = info?.info?.policyId;

  // Fetch sub-policy data when editing to get extracted PDF content
  useEffect(() => {
    const fetchSubPolicy = async () => {
      if (isOpen && isEditMode && policyId && subPolicyId) {
        try {
          setLoadingSubPolicy(true);
          const response = await getApi(
            `${API_PATHS.CREATE_COMPANY_POLICY}/${policyId}/sub-policies/${subPolicyId}`
          );
          setSubPolicyData(response.data);
        } catch (error) {
          console.error('Error fetching sub-policy:', error);
          toast.error('Failed to load sub-policy data');
        } finally {
          setLoadingSubPolicy(false);
        }
      } else {
        setSubPolicyData(null);
      }
    };

    fetchSubPolicy();
  }, [isOpen, isEditMode, policyId, subPolicyId]);

  const getValidationSchema = () => {
    return Yup.object().shape({
      title: Yup.string()
        .required("Sub-Policy name is required")
        .test('unique-title', isEditMode ? 'This Sub-Policy name already exists' : 'Sub-Policy name already exists', function (value) {
          if (!value) return true;
          const currentTitle = info?.info?.subPolicyInfo?.info?.title;
          const isDuplicate = data?.some(policy =>
            policy.title?.toLowerCase() === value.toLowerCase() &&
            (!isEditMode || policy.title !== currentTitle)
          );
          return !isDuplicate;
        }),
      description: Yup.string()
        .trim()
        .required("Description is required"),
      content: Yup.string()
        .trim()
        .test(
          "not-empty-trimmed",
          "Content is required",
          (value) => {
            if (!value) return false;
            const clean = value
              .replace(/<[^>]*>/g, "")
              .replace(/&nbsp;/g, " ")
              .trim();
            return clean.length > 0;
          }
        )
        .required("Content is required")
    });
  };

  function onCloseHandler() {
    onClose();
  }

  return (
    <Modal onClose={onCloseHandler} isOpen={isOpen} isCentered size="xl">
      <ModalOverlay />
      <ModalContent maxW="1000px" w="1000px">
        <Formik
          enableReinitialize
          key={isEditMode && subPolicyData ? `edit-${subPolicyData._id || subPolicyId}-${subPolicyData.content?.substring(0, 20) || ''}` : 'create'}
          initialValues={
            info.type === 'edit' ? {
              title: subPolicyData?.title ?? info?.info?.subPolicyInfo?.info?.title ?? "",
              description: subPolicyData?.description ?? info?.info?.subPolicyInfo?.info?.description ?? "",
              content: subPolicyData?.content ?? info?.info?.subPolicyInfo?.info?.content ?? "",
            } : {
              title: '',
              description: '',
              content: '',
            }
          }
          validationSchema={getValidationSchema()}
          onSubmit={async (values, formikHelpers) => {
            const { resetForm } = formikHelpers;

            try {
              await handleFormSubmit(
                {
                  ...values,
                  info: info?.info, // Include info object with policyId and subPolicyInfo
                },
                { resetForm }
              );
            } catch (error) {
              console.error("Form submission error:", error);
              toast.error("Failed to save subpolicy");
            }
          }}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({ setFieldValue, isValid, setFieldTouched }) => {
            return (
              <Form>
                <FormikDataUpdater
                  subPolicyData={subPolicyData}
                  isEditMode={isEditMode}
                  loadingSubPolicy={loadingSubPolicy}
                  setFieldValue={setFieldValue}
                />
                <ModalHeader>
                  {info.type === 'edit'
                    ? recruitConstant.createCompanySubPolicy.replace('Create', 'Edit')
                    : recruitConstant.createCompanySubPolicy}
                </ModalHeader>
                <IconButton
                  size="xs"
                  className="closeIcon"
                  onClick={onCloseHandler}
                  icon={<CloseIcon />}
                />

                <ModalBody>
                  {loadingSubPolicy && isEditMode ? (
                    <Flex justify="center" align="center" minH="200px">
                      <Spinner size="xl" />
                      <Text ml={4}>Loading sub-policy content...</Text>
                    </Flex>
                  ) : (
                    <>
                      <Flex gap={"20px"} p={5} pt={0}>
                        <Box className='field-container' w={"50%"}>
                          <FormLabel htmlFor="title">
                            {recruitConstant.policyName.replace('Policy', 'SubPolicy')}
                            <span className="astrick">*</span>
                          </FormLabel>
                          <Field name="title">
                            {({ field, meta }) => (
                              <>
                                <Input
                                  {...field}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.startsWith(' ') && e.target.value.trim() === '') return;
                                    setFieldValue("title", value);
                                  }}
                                  placeholder={recruitConstant.policyName.replace('Policy', 'SubPolicy')}
                                  borderColor={
                                    meta.touched && meta.error
                                      ? colors.errorText
                                      : colors.borderColor
                                  }
                                />
                                {meta.error && meta.touched && (
                                  <Text color={colors.errorText}>{meta.error}</Text>
                                )}
                              </>
                            )}
                          </Field>
                        </Box>
                        <Box className='field-container' w={"50%"}>
                          <FormLabel htmlFor="description">
                            {recruitConstant.description}
                            <span className="astrick">*</span>
                          </FormLabel>
                          <Field name="description">
                            {({ field, meta }) => (
                              <>
                                <Input
                                  {...field}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.startsWith(' ') && e.target.value.trim() === '') return;
                                    setFieldValue("description", value);
                                  }}
                                  placeholder={recruitConstant.description}
                                  borderColor={
                                    meta.touched && meta.error
                                      ? colors.errorText
                                      : colors.borderColor
                                  }
                                />
                                {meta.error && meta.touched && (
                                  <Text color={colors.errorText}>{meta.error}</Text>
                                )}
                              </>
                            )}
                          </Field>
                        </Box>
                      </Flex>

                      <Flex p={5} pb={0} pt={0}>
                        <Box className='field-container' w={"100%"}>
                          <FormLabel htmlFor="content">
                            {recruitConstant.content}
                            <span className="astrick">*</span>
                          </FormLabel>
                          <Field name="content">
                            {({ field, meta }) => (
                              <>
                                <CompanyPolicyEditor
                                  value={field.value || ''}
                                  onChange={(v) => {
                                    const textOnly = v
                                      .replace(/<[^>]*>/g, "")
                                      .replace(/&nbsp;/g, " ")
                                      .trim();

                                    const isEmpty = textOnly.length === 0;
                                    const finalValue = isEmpty ? "" : v;
                                    setFieldValue('content', finalValue);
                                    setFieldTouched('content');
                                  }}
                                />
                                {meta.error && meta.touched && !meta.value && (
                                  <Text
                                    mt={4}
                                    mb={0}
                                    fontWeight="400!important"
                                    fontSize="14px"
                                    borderRadius={'4px'}
                                    bg={'#f7b8b8'}
                                    color={'#000'}
                                    p={'8px'}
                                    textAlign={'left'}
                                    className="roboto"
                                  >
                                    {meta.error}
                                  </Text>
                                )}
                              </>
                            )
                            }
                          </Field>
                        </Box>
                      </Flex>
                    </>
                  )}
                </ModalBody>

                <ModalFooter gap={'16px'}>
                  <Button
                    className="theme-btn2 cancelBtn"
                    sx={{ textTransform: "capitalize" }}
                    onClick={onCloseHandler}
                    variant="outline"
                    colorScheme={colors.error["100"]}
                    size="sm"
                  >
                    {constant.cancel}
                  </Button>
                  <Button
                    disabled={addLoading || updateLoading || !isValid}
                    className="nextBtn theme-btn2"
                    variant="primary"
                    size="sm"
                    type="submit"
                  >
                    {addLoading || updateLoading ? (
                      <Spinner size="sm" />
                    ) : info.type === 'edit' ? (
                      recruitConstant.edit
                    ) : (
                      recruitConstant.create
                    )}
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default CompanySubPolicyModel;
